import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { getBenefitDetails } from 'core'
//
import {
  PerksCategoriesWithExpenseResponse,
  getCategoriesWithExpenseTypes,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getCategoriesWithExpenseTypes'
import { PerksProductClassExpenseCategoryAllowanceRequest } from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getPerksProductClassDesignWizardState'
import setPerksProductClassAllowances, {
  SetPerksProductClassAllowancesRequest,
} from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/setPerksProductClassAllowances'
import { frequencies } from 'core/domain/benefit/allowanceFrequency'

//
import FormikDefaultInputField from 'common/components/dataEntry/formik/FormikDefaultInputField'

//
import {
  Alert,
  Col,
  DefaultDatePickerV2,
  DefaultSelect,
  Icon,
  Option,
  Radio,
  Row,
  SimpleCheckbox,
  Skeleton,
} from 'common/components'
import { parseZone } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import { BaseComponentProps } from '../../../../../common/productClassWizardModal/Common'

import './styles.less'

export type SetProductClassAllowancesProps = {
  id: string
  categoryAllowances: PerksProductClassExpenseCategoryAllowanceRequest[]
  offerSameAllowance: boolean
}

export default (props: BaseComponentProps<SetProductClassAllowancesProps>) => {
  const { t: translate } = useTranslation()

  const { bindSubmitFormHandler } = props

  const { data: planPeriodDetails } = useQueryRequestV2({
    queryFn: () => getBenefitDetails(props.planPeriodId),
  })
  const { data: categories } = useQueryRequestV2<PerksCategoriesWithExpenseResponse[]>({
    queryFn: () => getCategoriesWithExpenseTypes(props.state.id, props.planPeriodId),
  })

  const { sendCommand: sendSetAllowancesCommand, isLoading: loading } = useCommandRequestV2(
    (request: SetPerksProductClassAllowancesRequest) => setPerksProductClassAllowances(request),
  )
  const initialProperties = (categoryAllowances: any[], offerSameAllowance: boolean) => {
    for (let i = 0; i < categoryAllowances.length; i++) {
      categoryAllowances[i].amount = null
      categoryAllowances[i].allowanceFrequency = frequencies[0].id
      categoryAllowances[i].foreverPerk = false
      categoryAllowances[i].startDate = planPeriodDetails?.startDate ?? null
      categoryAllowances[i].endDate = null
    }
    if (offerSameAllowance) return [categoryAllowances[0]]
    return categoryAllowances
  }

  const fillData = (categoryAllowances: any[]) => {
    for (let i = 0; i < categoryAllowances.length; i++) {
      categoryAllowances[i].startDate = planPeriodDetails?.startDate ?? null
      categoryAllowances[i].endDate = !categoryAllowances[i].foreverPerk ? categoryAllowances[i].endDate : null
    }
    return categoryAllowances
  }
  // @ts-ignore
  const [initialValues, setInitialValues] = useState<SetPerksProductClassAllowancesRequest>({
    categoryAllowances: fillData(props.state.categoryAllowances),
    offerSameAllowance: props.state.offerSameAllowance || false,
    productClassId: props.state.id,
  })

  // @ts-ignore

  const handleChange = (formik, newValue) => {
    formik.setFieldValue('offerSameAllowance', newValue)
    formik.setFieldValue(
      'categoryAllowances',
      initialProperties(formik.initialValues.categoryAllowances, props.editing),
    )
  }

  const disableEndDate = (formik, e, index) => {
    if (e.target.checked) {
      formik.setFieldValue(`categoryAllowances[${index}].endDate`, null)
    }
    formik.setFieldValue(`categoryAllowances[${index}].foreverPerk`, e.target.checked)
  }

  const onSubmit = async (values: any) => {
    const copyCategoryAllowances = values.categoryAllowances

    var updated = copyCategoryAllowances.map((el) => {
      if (el.foreverPerk) {
        return { ...el, endDate: planPeriodDetails.endDate }
      }
      return el
    })

    let request = {
      offerSameAllowance: values.offerSameAllowance,
      categoryAllowances: updated,
      productClassId: props.state.id,
    }

    const result = await sendSetAllowancesCommand(request)

    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  useEffect(() => {
    if (categories && categories.length > 0 && initialValues.categoryAllowances.length == 0 && planPeriodDetails) {
      const categoryAllowances: PerksProductClassExpenseCategoryAllowanceRequest[] = categories.map((item) => ({
        foreverPerk: false,
        expenseCategoryId: item.id,
        allowanceFrequency: frequencies[0].id,
        endDate: null,
        startDate: planPeriodDetails?.startDate ?? null,
      }))
      setInitialValues({ ...initialValues, categoryAllowances })
    }
  }, [categories, planPeriodDetails])

  useEffect(() => {
    if (planPeriodDetails) {
      initialValues.categoryAllowances = initialValues.categoryAllowances.map((el) => {
        if (new Date(el.endDate).getFullYear() === new Date(planPeriodDetails?.endDate).getFullYear()) {
          el.endDate = null
        }
        return el
      })
    }
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [initialValues, planPeriodDetails])

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={Yup.object({})}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="set-product-class-allowance-wrapper">
            <Skeleton loading={!categories}>
              {categories && (
                <>
                  <Row>
                    <Col span={20}>
                      <h5 className="select_class_info mb-24">
                        {translate('admin-dashboard.benefit-details.class-design.perks-set-allowances.header')}
                      </h5>
                    </Col>
                  </Row>
                  {categories?.length > 1 && (
                    <div onClick={() => handleChange(formik, true)}>
                      <Row className="select-allowance-amounts mb-8 pointer">
                        <Col>
                          <p className="mt-16">
                            {translate(
                              'admin-dashboard.benefit-details.class-design.perks-set-allowances.total-allowance',
                            )}{' '}
                            <span className="new-label">New</span>
                          </p>
                        </Col>
                        <Col>
                          <Radio.Group onChange={(e) => handleChange(formik, e.target.value)} name="offerSameAllowance">
                            <Radio value={true} />
                          </Radio.Group>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {formik.values.offerSameAllowance && categories?.length > 1 && (
                    <>
                      <div className={'categories-wrapper'}>
                        <div className={'single-category-wrapper'}>
                          <Row justify="space-between">
                            <Col xs={24} md={11}>
                              <FormikDefaultInputField
                                prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                                label={translate(
                                  'admin-dashboard.benefit-details.class-design.perks-set-allowances.input-label',
                                )}
                                placeholder={'0.00'}
                                onChange={(evt) => {
                                  const result = [...formik.values.categoryAllowances]
                                  for (let i = 0; i < result.length; i++) {
                                    result[i].amount =
                                      isNaN(parseFloat(evt.target.value)) || evt.target.value.length == 0
                                        ? null
                                        : parseInt(evt.target.value)
                                  }
                                  formik.setFieldValue('categoryAllowances', result)
                                }}
                                name={`categoryAllowances[0].amount`}
                              />
                              <DefaultDatePickerV2
                                className={'perk-start-date-picker'}
                                label={translate(
                                  'admin-dashboard.benefit-details.class-design.set-allowances.start-date',
                                )}
                                name={`categoryAllowances[0].startDate`}
                                id={`categoryAllowances[0].startDate`}
                                onChange={(date) => {
                                  if (date && formik.values.categoryAllowances[0]?.endDate) {
                                    formik.setFieldValue(`categoryAllowances[0].endDate`, null)
                                  }
                                }}
                                disabledDate={(d) =>
                                  !d || d.isBefore(moment(planPeriodDetails?.startDate).add(-1, 'days'))
                                }
                              />

                              <SimpleCheckbox
                                name={`categoryAllowances[0].foreverPerk`}
                                id={`categoryAllowances[0].foreverPerk`}
                                checked={formik.values.categoryAllowances[0]?.foreverPerk}
                                className="no-select checkbox-text"
                                onChange={(e) => {
                                  const result = [...formik.values.categoryAllowances]
                                  for (let i = 0; i < result.length; i++) {
                                    result[i].foreverPerk = e.target.checked
                                    if (e.target.checked) {
                                      result[i].endDate = null
                                    } else {
                                      result[i].endDate = result[0].endDate
                                    }
                                  }
                                  formik.setFieldValue('categoryAllowances', result)
                                }}
                              >
                                {translate('admin-dashboard.benefit-details.class-design.set-allowances.continuous')}
                              </SimpleCheckbox>
                            </Col>
                            <Col xs={24} md={12}>
                              <DefaultSelect
                                onChange={(evt) => {
                                  const result = [...formik.values.categoryAllowances]
                                  for (let i = 0; i < result.length; i++) {
                                    result[i].allowanceFrequency = parseInt(evt + '')
                                  }
                                  formik.setFieldValue('categoryAllowances', result)
                                }}
                                name={`categoryAllowances[0].allowanceFrequency`}
                                label={translate(
                                  'admin-dashboard.benefit-details.class-design.perks-set-allowances.frequency',
                                )}
                              >
                                {frequencies.map((item, index) => (
                                  <Option key={index} value={item.id}>
                                    {item.name}
                                  </Option>
                                ))}
                              </DefaultSelect>

                              <DefaultDatePickerV2
                                className={'perk-end-date-picker mb-16'}
                                label={translate(
                                  'admin-dashboard.benefit-details.class-design.set-allowances.end-date',
                                )}
                                name={`categoryAllowances[0].endDate`}
                                id={`categoryAllowances[0].endDate`}
                                disabled={!!formik.values.categoryAllowances[0]?.foreverPerk}
                                disabledDate={(d) =>
                                  !d ||
                                  d.isBefore(parseZone(formik.values.categoryAllowances[0].startDate).add(1, 'days'))
                                }
                              />
                            </Col>
                          </Row>
                        </div>
                        <Col span={24} className="mb-16">
                          <Alert
                            showIcon
                            message={translate(
                              'admin-dashboard.benefit-details.class-design.perks-set-allowances.message',
                            )}
                            description={translate(
                              'admin-dashboard.benefit-details.class-design.perks-set-allowances.description',
                            )}
                            type={'info'}
                          />
                        </Col>
                      </div>
                    </>
                  )}
                  {categories?.length > 1 && (
                    <div onClick={() => handleChange(formik, false)}>
                      <Row className="select-allowance-amounts mb-8 pointer">
                        <Col>
                          <p className="mt-16">
                            {translate(
                              'admin-dashboard.benefit-details.class-design.perks-set-allowances.different-allowances',
                            )}
                          </p>
                        </Col>
                        <Col>
                          <Radio.Group name="offerSameAllowance" onChange={(e) => handleChange(formik, e.target.value)}>
                            <Radio value={false} />
                          </Radio.Group>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {(!formik.values.offerSameAllowance || categories?.length === 1) && (
                    <div className={'categories-wrapper'}>
                      <p className={'color-gray-8 semi-bold mb-8'}>
                        {`${translate(
                          'admin-dashboard.benefit-details.class-design.perks-select-class.total-categories',
                        )}: ${categories?.length}`}
                      </p>
                      {categories?.map((item, index) => (
                        <div className={'single-category-wrapper'}>
                          <div className={'color-gray-9 semi-bold mb-16'}>
                            {item.name}
                            <span className={'category-counter color-gray-7'}>{`(${index + 1}/${
                              categories.length
                            })`}</span>
                          </div>
                          <Row justify={'space-between'} gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                              <FormikDefaultInputField
                                prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                                //  type={'number'}
                                label={translate(
                                  'admin-dashboard.benefit-details.' + 'class-design.perks-set-allowances.input-label',
                                )}
                                placeholder={'0.00'}
                                name={`categoryAllowances[${index}].amount`}
                              />
                              <DefaultDatePickerV2
                                className={'perk-start-date-picker'}
                                label={translate(
                                  'admin-dashboard.benefit-details.class-design.set-allowances.start-date',
                                )}
                                name={`categoryAllowances[${index}].startDate`}
                                id={`categoryAllowances[${index}].startDate`}
                                onChange={(date) => {
                                  if (date) {
                                    formik.setFieldValue(`categoryAllowances[${index}].endDate`, null)
                                  }
                                }}
                                disabledDate={(d) =>
                                  !d || d.isBefore(moment(planPeriodDetails?.startDate).add(-1, 'days'))
                                }
                              />
                            </Col>
                            <Col xs={24} md={12}>
                              <DefaultSelect
                                name={`categoryAllowances[${index}].allowanceFrequency`}
                                label={'Choose frequency'}
                              >
                                {frequencies.map((item, index) => (
                                  <Option key={index} value={item.id}>
                                    {item.name}
                                  </Option>
                                ))}
                              </DefaultSelect>

                              <DefaultDatePickerV2
                                className={'perk-end-date-picker'}
                                label={translate(
                                  'admin-dashboard.benefit-details.class-design.set-allowances.end-date',
                                )}
                                name={`categoryAllowances[${index}].endDate`}
                                id={`categoryAllowances[${index}].endDate`}
                                disabled={!!formik.values.categoryAllowances[index]?.foreverPerk}
                                disabledDate={(d) =>
                                  !d ||
                                  d.isBefore(
                                    parseZone(formik.values.categoryAllowances[index].startDate).add(1, 'days'),
                                  )
                                }
                              />
                            </Col>
                          </Row>
                          <SimpleCheckbox
                            name={`categoryAllowances[${index}].foreverPerk`}
                            className="no-select checkbox-text"
                            checked={formik.values.categoryAllowances[index]?.foreverPerk}
                            onChange={(e) => {
                              disableEndDate(formik, e, index)
                            }}
                          >
                            {translate('admin-dashboard.benefit-details.class-design.set-allowances.continuous')}
                          </SimpleCheckbox>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Skeleton>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    categoryAllowances: Yup.array().of(
      Yup.object().shape({
        amount: Yup.number()
          .min(1, translate('admin-dashboard.benefit-details.class-design.errors.positive-number'))
          .typeError(translate('admin-dashboard.benefit-details.class-design.errors.enter-amount'))
          .required(translate('general.required')),
        allowanceFrequency: Yup.number().required(translate('general.required')),
        startDate: Yup.date().required(translate('general.required')),
        endDate: Yup.mixed().when('foreverPerk', {
          is: true,
          then: Yup.date().nullable(),
          otherwise: Yup.date().required('End date is required'),
        }),
      }),
    ),
  })
